import { zodParse } from 'base/zod_parse';
import { z } from 'zod';
import { HttpEngine } from './auth_service';
import { ServiceHttpError } from './service_error';
import { Locale, LOCALE_INFO } from './ui/locales';
import * as webflowSchema from './webflow/schema';

// relume and finsweet are the latest versions (both 2.1). relumev2 and finsweetv2 are legacy
export type Style =
  | 'relume'
  | 'relumev2'
  | 'relumev21'
  | 'finsweet'
  | 'finsweetv2'
  | 'blocks';
export type SpacingStrategy = 'wrappers' | 'blocks';

export type FilterKindValue =
  | 'component'
  | 'ui-element'
  | 'page-template'
  | undefined;
export type FindComponentsQuery = Readonly<{
  mode?: 'saved' | 'recently-used';
  filterKind?: FilterKindValue;
  filterFree?: boolean;
  filterCategory?: string[];
  filterBlocksCategoryUser?: string[];
  filterBlocksCategoryAi?: string[];
  filterComponentSlug?: string[];
  search?: string;
  limit?: number;
  includeNotMatchingSearch?: boolean;
  includeListitemHtml?: boolean;
  excludeAppComponents?: boolean;
}>;
export type FindCategoriesQuery = Readonly<{
  filterKind?: FilterKindValue;
  includeComponents?: number;
}>;

type CopyPublicComponentRequest = {
  componentSlug: string;
  trigger: string;
  /** Don't make an analytics event or recently used history entry */
  noCopyEvent?: boolean;
  analyticsStyle: Style | undefined;
  analyticsSpacing: SpacingStrategy | undefined;
};

export interface LibraryService {
  findPublicComponents(
    query: FindComponentsQuery,
    continuation?: string,
  ): Promise<{
    components: PublicComponent[];
    continuation?: string;
  }>;

  findPublicCategories(query: FindCategoriesQuery): Promise<{
    categories: Category[];
  }>;

  findBlocksCategories(): Promise<FindBlocksCategoriesResponse>;

  getHostedFolder(folderId: string | undefined): Promise<{
    folder: Folder;
    components: HostedComponent[];
    folders: Folder[];
  }>;

  findRecentlyUsedHostedComponents(): Promise<{
    components: HostedComponent[];
  }>;

  searchHostedLibrary(query: string): Promise<{
    components: HostedComponent[];
    folders: Folder[];
  }>;

  /** Gets the component content, without logging a copy event. */
  getPublicComponentJson(
    componentSlug: string,
    opts?: { bypassCloudfront?: boolean },
  ): Promise<webflowSchema.Component>;

  listSlotTypes(): Promise<ListSlotTypesResponse>;

  getSlotType(id: string): Promise<SlotType>;

  getElementType(id: string): Promise<ElementType>;

  listElementTypes(): Promise<ListElementTypesResponse>;

  getLibraryElement(id: string): Promise<LibraryElement>;

  listLibraryElements(): Promise<ListLibraryElementsResponse>;

  /** Logs a copy event and returns the component content */
  copyPublicComponent(
    request: CopyPublicComponentRequest,
  ): Promise<webflowSchema.Component>;

  copyHostedComponent(component: HostedComponent): Promise<unknown>;
}

export class HttpLibraryService implements LibraryService {
  private useStagingComponents: boolean;

  constructor(
    private readonly http: HttpEngine,
    opts?: { useStagingComponents?: boolean },
  ) {
    this.useStagingComponents = opts?.useStagingComponents ?? false;
  }

  async findPublicComponents(
    query: FindComponentsQuery,
    continuation?: string,
  ) {
    const params = new URLSearchParams({
      member_token: await this.http.getTokenLegacy(),
    });
    if (query.mode) params.append('mode', query.mode);
    if (query.filterKind) params.append('filter_kind', query.filterKind);
    if (query.filterFree) params.append('filter_free', 'true');
    for (const category of query.filterCategory || [])
      params.append('filter_category', category);
    for (const category of query.filterBlocksCategoryUser || [])
      params.append('filter_blocks_category_user', category);
    for (const category of query.filterBlocksCategoryAi || [])
      params.append('filter_blocks_category_ai', category);
    for (const slug of query.filterComponentSlug || [])
      params.append('filter_component_slug', slug);
    if (query.search) params.append('search', query.search);
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.includeNotMatchingSearch)
      params.append('include_not_matching_search', 'true');
    if (query.includeListitemHtml)
      params.append('include_listitem_html', 'true');
    if (query.excludeAppComponents) {
      [
        'cookie-consent',
        'signup-login-modals',
        'contact-modals',
        'category-filters',
        'tables',
        'loaders',
        'onboarding-forms',
        'application-shells',
        'sidebars',
        'topbars',
        'page-headers',
        'section-headers',
        'card-headers',
        'stat-cards',
        'stacked-lists',
        'grid-lists',
        'forms',
        'description-lists',
      ].forEach((category) => params.append('exclude_categories', category));
    }
    if (continuation) params.append('continuation', continuation);
    const url = `/v1/components?${params}`;

    const j = await this.http.request(
      'GET',
      url,
      undefined,
      findComponentsResponseSchema,
    );
    return {
      folders: [],
      components: j.components.map<PublicComponent>((c) => ({
        ...c,
        from: 'public-library',
      })),
      continuation: j.continuation,
    };
  }

  async findPublicCategories(query: FindCategoriesQuery) {
    const params = new URLSearchParams({
      member_token: await this.http.getTokenLegacy(),
    });
    if (query.filterKind) params.append('filter_kind', query.filterKind);
    if (query.includeComponents)
      params.append('include_components', query.includeComponents.toString());
    const url = `/v1/categories?${params}`;
    const j = await this.http.request(
      'GET',
      url,
      undefined,
      findCategoriesResponseSchema,
    );
    return {
      categories: j.categories.map((cat) => ({
        ...cat,
        components: cat.components.map<PublicComponent>((comp) => ({
          ...comp,
          from: 'public-library',
        })),
      })),
    };
  }

  async findBlocksCategories() {
    const url = `/v1/blocks_categories`;
    return this.http.request(
      'GET',
      url,
      undefined,
      findBlocksCategoriesResponseSchema,
    );
  }

  async getHostedFolder(folderId: string | undefined) {
    const params = new URLSearchParams({
      jwt: await this.http.getTokenLegacy(),
    });
    if (folderId) params.append('folder_id', folderId);
    const url = `/hosted/v1/folder/?${params}`;

    const j = await this.http.request('GET', url, undefined, getFolderSchema);
    return {
      folder: j.folder_summary,
      folders: j.folders,
      components: j.components.map(mapHostedComponent),
    };
  }

  async findRecentlyUsedHostedComponents() {
    const params = new URLSearchParams({
      jwt: await this.http.getTokenLegacy(),
    });
    const url = `/hosted/v1/folder/recently_used/?${params}`;

    const j = await this.http.request(
      'GET',
      url,
      undefined,
      findRecentlyUsedHostedComponentsSchema,
    );
    return { components: j.components.map(mapHostedComponent) };
  }

  async searchHostedLibrary(query: string) {
    const params = new URLSearchParams({
      jwt: await this.http.getTokenLegacy(),
      query,
    });
    const url = `/hosted/v1/search/?${params}`;

    const j = await this.http.request(
      'GET',
      url,
      undefined,
      searchHostedLibrarySchema,
    );
    return {
      folders: j.folders,
      components: j.components.map(mapHostedComponent),
    };
  }

  async getPublicComponentJson(
    componentSlug: string,
    opts?: { bypassCloudfront?: boolean },
  ) {
    const key = `/scrambled${
      this.useStagingComponents ? '/staging' : ''
    }/${componentSlug}.json`;
    const url = opts?.bypassCloudfront
      ? // by default s3 sends no cache-control header,
        // which causes the browser to cache responses for a heuristic amount of time (so dumb)
        // "response-cache-control" isn't supported on anonymous get requests
        `https://s3.amazonaws.com/components-public.relume.io${key}?_=${new Date().getTime()}`
      : `https://components-public.relume.io${key}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new ServiceHttpError(
        res.status,
        await res.text(),
        'components-public',
      );
    }
    return zodParse(
      webflowSchema.componentSchema,
      unscramble(await res.json()),
    );
  }

  async listSlotTypes() {
    return this.http.request(
      'GET',
      '/v1/slot_types',
      undefined,
      listSlotTypesResponseSchema,
    );
  }

  async getSlotType(id: string) {
    return this.http.request(
      'GET',
      `/v1/slot_type/${id}`,
      undefined,
      slotTypeSchema,
    );
  }

  async listElementTypes() {
    return this.http.request(
      'GET',
      '/v1/element_types',
      undefined,
      listElementTypesResponseSchema,
    );
  }

  async getElementType(id: string) {
    return this.http.request(
      'GET',
      `/v1/element_type/${id}`,
      undefined,
      elementTypeSchema,
    );
  }

  async getLibraryElement(id: string) {
    return this.http.request(
      'GET',
      `/v1/element/${id}`,
      undefined,
      libraryElementSchema,
    );
  }

  async listLibraryElements() {
    return this.http.request(
      'GET',
      '/v1/elements',
      undefined,
      listLibraryElementsSchema,
    );
  }

  async copyPublicComponent(req: CopyPublicComponentRequest) {
    const params = new URLSearchParams({
      member_token: await this.http.getTokenLegacy(),
    });
    if (req.noCopyEvent) params.append('no_copy_event', 'true');
    if (req.analyticsStyle)
      params.append('analytics_theme', req.analyticsStyle);
    if (req.analyticsSpacing)
      params.append('analytics_spacing', req.analyticsSpacing);
    if (req.trigger) params.append('trigger', req.trigger);
    const url = `/v1/component/relume/ignored/${req.componentSlug}/?${params}`;
    const res = await this.http.request('GET', url, undefined);
    if (typeof res === 'object' && res && (res as any)?.status === 'fail') {
      throw new Error((res as any).reason || '');
    }
    return zodParse(webflowSchema.componentSchema, res);
  }

  async copyHostedComponent(component: HostedComponent) {
    const params = new URLSearchParams({
      component_id: component.id,
      jwt: await this.http.getTokenLegacy(),
      trigger: 'chrome-extension-webflow',
    });
    const url = `/hosted/v1/component/?${params}`;
    const res = await this.http.request(
      'GET',
      url,
      undefined,
      copyHostedComponentSchema,
    );
    return JSON.parse(res.component_data);
  }
}

export type Component = {
  name: string;
  thumbnailUrl: string | undefined;
  thumbnailWidth: number;
  thumbnailHeight: number;
  // Undefined means it wasn't fetched (not that it is unused)
  lastUsedTime?: number;
} & (
  | {
      from: 'public-library';
      componentSlug: string;
      categorySlug: string;
      category?: string;
      webflowSlug: string;
      kind: ComponentKind;
      listitemHtml?: string;
      variants: VariantGroup[];
    }
  | {
      from: 'hosted-library';
      id: string;
      kind?: undefined;
    }
);
export type PublicComponent = Component & { from: 'public-library' };
export type HostedComponent = Component & { from: 'hosted-library' };
export type ComponentKind = z.TypeOf<typeof componentKindSchema>;

function mapHostedComponent(
  c: z.TypeOf<typeof hostedComponentSchema>,
): HostedComponent {
  return {
    from: 'hosted-library',
    id: c.component_id,
    name: c.name,
    thumbnailUrl: c.image !== 'placeholder' ? c.image : undefined,
    thumbnailWidth: c.image_width || 1,
    thumbnailHeight: c.image_height || 1,
    lastUsedTime: undefined,
  };
}

function nullToUndefined<T>(v: T | null | undefined): T | undefined {
  if (v == null) {
    return undefined;
  }
  return v;
}

const componentKindSchema = z.enum([
  'component',
  'ui-element',
  'page-template',
]);
const variantGroupSchema = z.object({
  // variant group kind, e.g. "asset type"
  kind: z.string(),
  items: z.array(
    z.object({
      // variant item kind, e.g. "image" or "video"
      kind: z.string(),
      componentSlug: z.string(),
    }),
  ),
});
export type VariantGroup = z.infer<typeof variantGroupSchema>;
const publicComponentSchema = z.object({
  kind: componentKindSchema,
  categorySlug: z.string(),
  category: z.string().nullish().transform(nullToUndefined),
  componentSlug: z.string(),
  webflowSlug: z.string(),
  name: z.string(),
  thumbnailUrl: z.string(),
  thumbnailWidth: z.number(),
  thumbnailHeight: z.number(),
  lastUsedTime: z.number().nullish().transform(nullToUndefined),
  listitemHtml: z.string().nullish().transform(nullToUndefined),
  variants: variantGroupSchema.array().default([]),
});
const findComponentsResponseSchema = z.object({
  components: z.array(publicComponentSchema),
  continuation: z.string().nullish().transform(nullToUndefined),
});

const categorySchema = z.object({
  categorySlug: z.string(),
  name: z.string(),
  kind: componentKindSchema,
  components: z.array(publicComponentSchema).default([]),
});
const findCategoriesResponseSchema = z.object({
  categories: z.array(categorySchema),
});

const translationsMapSchema = z.record(z.string(), z.string()).transform(
  (dict) =>
    new Map(
      Object.entries(dict).filter((tuple): tuple is [Locale, string] => {
        const [locale, string] = tuple;
        if (!(locale in LOCALE_INFO)) {
          console.warn(
            'Backend provide unknown locale, ignoring',
            locale,
            string,
          );
          return false;
        }
        return true;
      }),
    ),
);

const blocksCategorySchema = z.object({
  name: z.string(),
  translations: translationsMapSchema,
  includeInGeneratePage: z.boolean(),
});
export type BlocksCategory = z.TypeOf<typeof blocksCategorySchema>;
const findBlocksCategoriesResponseSchema = z.object({
  blocksCategories: z.array(blocksCategorySchema),
  homeTranslations: translationsMapSchema,
});
export type FindBlocksCategoriesResponse = z.TypeOf<
  typeof findBlocksCategoriesResponseSchema
>;

const slotTypeIdSchema = z.custom<`ST${string}`>(
  (v) => typeof v === 'string' && v.startsWith('ST'),
);

const elementTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  axes: z.array(
    z.object({
      name: z.string(),
      hidden: z.boolean(),
      options: z.array(
        z.object({
          name: z.string(),
        }),
      ),
    }),
  ),
  elements: z.array(
    z.object({
      id: z.string(),
      typeValues: z.array(
        z.object({
          axis: z.object({
            name: z.string(),
          }),
          option: z.object({
            name: z.string(),
          }),
        }),
      ),
    }),
  ),
});
export type ElementType = z.TypeOf<typeof elementTypeSchema>;

const listElementTypesResponseSchema = z.object({
  elementTypes: z.array(elementTypeSchema),
});
export type ListElementTypesResponse = z.TypeOf<
  typeof listElementTypesResponseSchema
>;

const libraryElementSchema = z.object({
  id: z.string(),
  name: z.string(),
  thumbnailUrl: z.string(),
  thumbnailWidth: z.number(),
  thumbnailHeight: z.number(),
  /* IDs of the SlotTypes which the element is allowed in */
  allowedIn: z.array(slotTypeIdSchema),
  defaultMargin: z
    .object({
      direction: z.enum([
        'top',
        'right',
        'bottom',
        'left',
        'vertical',
        'horizontal',
        'all',
      ]),
      size: z.enum([
        'tiny',
        'xxsmall',
        'xsmall',
        'small',
        'medium',
        'large',
        'xlarge',
        'xxlarge',
        'huge',
        'xhuge',
        'xxhuge',
      ]),
    })
    .nullable()
    .transform(nullToUndefined),
  elementType: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
});
export type LibraryElement = z.TypeOf<typeof libraryElementSchema>;

const listLibraryElementsSchema = z.object({
  elements: z.array(libraryElementSchema),
});
export type ListLibraryElementsResponse = z.TypeOf<
  typeof listLibraryElementsSchema
>;

const slotTypeSummarySchema = z.object({
  id: slotTypeIdSchema,
  name: z.string(),
  minItems: z.number(),
  maxItems: z.number().nullable(),
  itemMargins: z.enum(['none', 'vertical']),
  dragHandlePosition: z.enum(['top', 'left']),
  defaultMarginSize: z.enum([
    'tiny',
    'xxsmall',
    'xsmall',
    'small',
    'medium',
    'large',
    'xlarge',
    'xxlarge',
    'huge',
    'xhuge',
    'xxhuge',
  ]),
});

export type SlotTypeSummary = z.TypeOf<typeof slotTypeSummarySchema>;

const listSlotTypesResponseSchema = z.object({
  slotTypes: z.array(slotTypeSummarySchema),
});

export type ListSlotTypesResponse = z.TypeOf<
  typeof listSlotTypesResponseSchema
>;

const slotTypeSchema = slotTypeSummarySchema.extend({
  allowedElements: z.array(libraryElementSchema),
});

export type SlotType = z.TypeOf<typeof slotTypeSchema>;

const folderSchema = z.object({
  folder_id: z.string(),
  name: z.string(),
  description: z.string(),
  owner_name: z.string(),
  role: z.enum(['admin', 'editor', 'copier', 'viewer']),
  parent_id: z.string().nullish().transform(nullToUndefined),
  parent_name: z.string().nullish().transform(nullToUndefined),
});
const hostedComponentSchema = z.object({
  name: z.string(),
  description: z.string(),
  folder_id: z.string(),
  folder_name: z.string(),
  component_id: z.string(),
  image: z.string(), // or "placeholder"
  image_width: z.number().nullish().transform(nullToUndefined),
  image_height: z.number().nullish().transform(nullToUndefined),
  owner: z.string(),
});
const getFolderSchema = z.object({
  status: z.literal('success'),
  folder_summary: folderSchema,
  components: z.array(hostedComponentSchema),
  folders: z.array(folderSchema),
});
const findRecentlyUsedHostedComponentsSchema = z.object({
  status: z.literal('success'),
  components: z.array(hostedComponentSchema),
});
const searchHostedLibrarySchema = z.object({
  status: z.literal('success'),
  components: z.array(hostedComponentSchema),
  folders: z.array(folderSchema),
});
const copyHostedComponentSchema = z.object({
  name: z.string(),
  component_id: z.string(),
  component_data: z.string(),
});

export type Folder = z.TypeOf<typeof folderSchema>;
export type Category = Omit<z.TypeOf<typeof categorySchema>, 'components'> & {
  components: PublicComponent[];
};

export function scramble(data: unknown): unknown {
  if (typeof data === 'string') {
    return data
      .split('')
      .map((c, i) => String.fromCharCode(c.charCodeAt(0) + 2 + (i % 10)))
      .join('');
  }
  if (typeof data === 'boolean') return !data;
  if (typeof data === 'number') return data + 1;
  if (data == null) return data;

  if (Array.isArray(data)) {
    return data.map(scramble);
  }
  if (typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [scramble(k), scramble(v)]),
    );
  }
  return data;
}

export function unscramble(data: unknown): unknown {
  if (typeof data === 'string') {
    return data
      .split('')
      .map((c, i) => String.fromCharCode(c.charCodeAt(0) - 2 - (i % 10)))
      .join('');
  }
  if (typeof data === 'boolean') return !data;
  if (typeof data === 'number') return data - 1;
  if (data == null) return data;

  if (Array.isArray(data)) {
    return data.map(unscramble);
  }
  if (typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [unscramble(k), unscramble(v)]),
    );
  }
  return data;
}
